import { FC, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { AddEventButton } from "Bands/containers";

import {
  useBandContext,
  useBandSubscriptionContext,
  useCalendarContext,
  usePermissionsContext,
  ViewKeys,
} from "Bands/contexts";
import {
  CalendarListView,
  CalendarPicker,
  CalendarView,
  EditBlock,
  TodayAction,
  ViewToggle,
} from "Bands/pages/calendar/containers";
import { clearDataByKey, dataMetaSelector, dispatchGetEvents } from "Bands/state";
import { Spacer } from "shared/layout";
import { CenterContent } from "shared/styles";
import { BreakpointSize, ContentWidth } from "shared/styles/constants";
import { IAPIState, IDataKeys } from "shared/types";

import { useBreakpoint } from "shared/utils";
import { StyledCalendarTools } from "./styles";

import { ExportXlsButton } from "Bands/containers/ExportXls/ExportXlsButton";
import { ExportXlsModal } from "Bands/containers/ExportXls/ExportXlsModal";
import { BAND_ACTIONS } from "Bands/types";
import { Loader } from "shared/components";
import { SessionStorageService } from "shared/services/SessionStorageService";

export const CalendarPage: FC = () => {
  const dispatch = useDispatch();
  const isMobile = useBreakpoint(BreakpointSize.l);
  const { bandId } = useBandContext();
  const { userCanAddEvent } = usePermissionsContext();
  const { view, start, stop, today, date } = useCalendarContext();
  const { currentSubscription, isActiveSubscription } = useBandSubscriptionContext();

  const [showXlsModal, setShowXlsModal] = useState(false);

  const { api } = useSelector(dataMetaSelector(BAND_ACTIONS.getEvents));

  useEffect(() => {
    dispatchGetEvents(dispatch, bandId ?? undefined, start, stop);
  }, [start, stop, bandId, dispatch]);

  useEffect(() => {
    return () => {
      dispatch(clearDataByKey(IDataKeys.calendar));
    };
  }, [dispatch]);

  const showTodayButton = date.month !== today.getMonth() || date.year !== today.getFullYear();

  useEffect(() => {
    SessionStorageService.setItem("month", date.month);
    SessionStorageService.setItem("year", date.year);
  }, [date]);

  const isLoading = api?.status === IAPIState.pending;

  return (
    <>
      {showXlsModal && <ExportXlsModal handleClose={() => setShowXlsModal(false)} />}
      <CenterContent width={ContentWidth.wide}>
        <Spacer size="l">
          <EditBlock />
          <StyledCalendarTools>
            <Spacer spaceDirection="horizontal" size="l">
              <CalendarPicker />
              <TodayAction disabled={!showTodayButton} />
              {!isMobile && <ViewToggle />}
            </Spacer>
            <Spacer spaceDirection="horizontal" size="m">
              {bandId && (currentSubscription || isActiveSubscription) && (
                <ExportXlsButton handleClick={() => setShowXlsModal(true)} />
              )}
              {!isLoading && bandId && userCanAddEvent && <AddEventButton />}
            </Spacer>
          </StyledCalendarTools>
          {isLoading ? (
            <Loader text="Loading events..." />
          ) : (
            <>
              {!isMobile && view === ViewKeys.calendar && <CalendarView />}
              {(isMobile || view === ViewKeys.list) && <CalendarListView />}
            </>
          )}
        </Spacer>
      </CenterContent>
    </>
  );
};
